import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, IconButton } from '@mui/material';
import { getGridStringOperators } from '@mui/x-data-grid';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { platformList } from '../../constants/platformList';
import { GRAINID_GRAIN_MAP, getGrainHeader } from '../../helpers/pricing';
import { filterNameOrAlias } from '../../helpers/utils';
import { PlatformLogo } from '../atoms/PlatformLogo';
import StyledChip from '../atoms/StyledChip';
import StyledDataGrid from '../atoms/StyledDataGrid';
import DataGridToolbar from '../molecules/DataGridToolbar';
import PlatformCell from '../molecules/PlatformCell';
import PlatformMultiSelect from '../molecules/PlatformMultiSelect';

const DEFAULT_RESIZABLE = true;

export const PricingTable = ({
    rows,
    config,
    setSelectedRow,
    setGrain,
    setOpen,
    setPricingRules,
}) => {
    const [selectedPage, setSelectedPage] = useState(0);
    const [selectedPageSize, setSelectedPageSize] = useState(10);
    const [dimensionPlatforms, setDimensionPlatforms] = useState([]);

    useEffect(() => {
        const allGrains = rows?.flatMap(row => row.grains || []) || [];
        const uniquePlatforms = [...new Set(allGrains.map(grain => grain.platform))].filter(
            Boolean,
        );
        setDimensionPlatforms(uniquePlatforms);
    }, [rows]);

    const handleEdit = rowToEdit => {
        const grain = get(GRAINID_GRAIN_MAP, rowToEdit.grain, null);
        const selectedRow = rows?.find(row => row.id === rowToEdit.id);
        setSelectedRow(selectedRow);
        setGrain(grain);
        setOpen(true);
    };

    const handleDelete = rowToDelete => {
        const grain = get(GRAINID_GRAIN_MAP, rowToDelete.grain, null);
        setPricingRules(oldPricingRules => {
            const newPricingRules = { ...oldPricingRules };
            newPricingRules[grain] = newPricingRules[grain].filter(
                rule => rule.id !== rowToDelete.id,
            );

            return newPricingRules;
        });
    };

    const generateColumns = grain => {
        const columns = [
            {
                field: 'calculation_type',
                headerName: 'Adjustment Type',
                width: 150,
                renderCell: ({ row }) => (
                    <StyledChip
                        label={row.calculation_type.name}
                        key={row.id}
                        sx={{ textTransform: 'none' }}
                    />
                ),
            },
            {
                field: 'platform',
                hideable: true,
                resizable: DEFAULT_RESIZABLE,
                headerName: 'Platform',
                width: 80,
                headerAlign: 'center',
                align: 'center',
                headerClassName: DEFAULT_RESIZABLE ? '' : 'non-resizable',
                filterOperators: getGridStringOperators()
                    .filter(operator => operator.value === 'isAnyOf')
                    .map(operator => ({
                        ...operator,
                        InputComponent: props => (
                            <PlatformMultiSelect {...props} platforms={dimensionPlatforms} />
                        ),
                        getApplyFilterFn: filterItem => {
                            if (!filterItem.value || filterItem.value.length === 0) {
                                return null;
                            }
                            return params => {
                                const rowPlatforms = [
                                    ...new Set(params.row.grains?.map(grain => grain.platform)),
                                ].filter(Boolean);
                                return filterItem.value.some(selectedPlatform =>
                                    rowPlatforms.includes(selectedPlatform),
                                );
                            };
                        },
                    })),
                valueGetter: ({ row }) => {
                    const platforms = [...new Set(row.grains?.map(grain => grain.platform))].filter(
                        Boolean,
                    );
                    return platforms.length === 1 ? platforms[0] : platforms.join(', ');
                },
                renderCell: ({ row }) => {
                    const platforms = [...new Set(row.grains?.map(grain => grain.platform))].filter(
                        Boolean,
                    );

                    if (platforms.length === 0) {
                        return null;
                    }

                    if (platforms.length === 1) {
                        return <PlatformCell row={{ platform: platforms[0] }} />;
                    }

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                            }}
                        >
                            {platforms.map(platform => (
                                <Box key={platform}>
                                    <PlatformCell row={{ platform }} />
                                </Box>
                            ))}
                        </Box>
                    );
                },
            },
            {
                field: 'names',
                headerName: getGrainHeader(grain),
                minWidth: 250,
                width: 500,
                getApplyQuickFilterFn:
                    searchTerm =>
                    ({ row }) =>
                        row.grains
                            .map(({ name }) => filterNameOrAlias(name, name, searchTerm))
                            .some(val => !!val),
                renderCell: ({ row }) => {
                    return (
                        <Grid container>
                            {row.grains?.map(({ id, platform_grain_id, name, alias, platform }) => {
                                return (
                                    <StyledChip
                                        key={platform_grain_id || id}
                                        label={alias?.config?.alias || name}
                                        sx={{
                                            mr: '5px',
                                            mb: '5px',
                                            mt: '5px',
                                            textTransform: 'none',
                                            '&&:hover': { backgroundColor: 'white' },
                                        }}
                                        icon={
                                            <Box>
                                                <PlatformLogo
                                                    platform={platformList[platform]}
                                                    placement={'top'}
                                                />
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Grid>
                    );
                },
            },
            {
                field: 'value',
                headerName: 'Value',
                flex: 1,
                valueGetter: ({ row }) => {
                    if (!row.value) {
                        return;
                    }

                    return row.calculation_type.symbol === '$' ? `$${row.value}` : `${row.value}%`;
                },
            },
            { field: 'start_date', headerName: 'Effective Date', flex: 1 },
            {
                field: 'actions',
                headerName: 'Actions',
                headerAlign: 'center',
                renderCell: params => (
                    <Grid container alignItems='center'>
                        <IconButton aria-label='edit' onClick={() => handleEdit(params.row)}>
                            <EditIcon />
                        </IconButton>
                        <IconButton aria-label='delete' onClick={() => handleDelete(params.row)}>
                            <DeleteIcon />
                        </IconButton>
                    </Grid>
                ),
            },
        ];
        return columns;
    };

    return (
        <StyledDataGrid
            rows={rows}
            getRowHeight={() => 'auto'}
            columns={generateColumns(config.grain)}
            rowsPerPageOptions={[5, 10, 20]}
            pageSize={selectedPageSize}
            page={selectedPage}
            onPageChange={page => {
                setSelectedPage(page);
            }}
            onPageSizeChange={pageSize => setSelectedPageSize(pageSize)}
            components={{
                Toolbar: () => DataGridToolbar({ columnHide: true }),
            }}
            componentsProps={{
                toolbar: {
                    quickFilterProps: { debounceMs: 500 },
                },
            }}
            localeText={{
                toolbarFilters: '',
                toolbarColumns: 'Add Fields',
            }}
            hideFooterSelectedRowCount
            disableDensitySelector
            disableRowSelectionOnClick
            autoHeight={true}
            sx={{
                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '10px' },
            }}
        />
    );
};
